<template>
    <div class="modal-mask">
        <div class="modal-wrapper">
            <div class="modal-container" style="width: 40%; min-width: 550px; max-width: 700px;">
                <div class="modal-header">
                    <h3 class="form-header">ePrescribing Setup</h3>
                    <button @click="closeModal(false)" class="red-close-button">
                        <i class="pi pi-times" style="padding: 0"></i>
                    </button>
                </div>
                <div class="modal-body">
                    <form class="action-modal" @submit.prevent>

                        <!-- STEP 1: HOW REGISTRATION WORKS -->
                        <!-- STEP 1: HOW REGISTRATION WORKS -->
                        <div v-if="step === 1" style="text-align: left; padding: 0 20px;">
                            <p style="font-size: 1em;">
                                Before you can send electronic prescriptions, you need to be registered with the
                                <b>eRx</b> electronic prescribing exchange. Here's how that works:
                            </p>
                            <ol style="font-size: 1em; padding-left: 20px;">
                                <li>Register at <a href="https://www.erx.com.au/register" target="_blank" rel="noopener">www.erx.com.au/register</a>.
                                    If you're registering multiple prescribers at the same site, click
                                    "Register another (similar) user" after your first registration.</li>
                                <li>eRx will create an <b>Entity ID</b> for your registration and email the
                                    registration details (including your Entity ID) to us, your software vendor.</li>
                                <li>We will confirm your <b>NASH PKI certificate</b> details (Organisation Name and
                                    Thumbprint) with eRx.</li>
                                <li>Once eRx activates your registration, we configure your Entity ID here.</li>
                            </ol>
                            <p style="font-size: 1em;">
                                If you already have your Entity ID and NASH certificate (<code>.p12</code> file) to
                                hand, you can enter them now. Otherwise, you can skip this step and come back to it
                                later.
                            </p>
                        </div>

                        <!-- STEP 2: CREDENTIALS -->
                        <!-- STEP 2: CREDENTIALS -->
                        <div v-if="step === 2" style="text-align: left; padding: 0 20px; display: grid; grid-gap: 1em;">
                            <p style="font-size: 1em; margin: 0;">
                                Both fields below are optional - only fill in what you have available right now.
                            </p>
                            <div class="form-field wide-text-field">
                                <label>Entity ID</label>
                                <input type="text" v-model="entityId" placeholder="Enter Entity ID (if known)">
                            </div>
                            <div class="form-field wide-text-field">
                                <label>NASH Certificate (.p12 / .pfx)</label>
                                <input type="file" accept=".p12,.pfx" @change="onCertFileSelected">
                                <p v-if="certFileName" style="font-size: 0.9em; margin: 4px 0 0;">Selected: {{ certFileName }}</p>
                            </div>
                            <div class="form-field wide-text-field">
                                <label>Certificate Passphrase</label>
                                <input type="password" v-model="certPassphrase" placeholder="Enter certificate passphrase">
                            </div>
                            <p v-if="errorMessage" style="color: red; font-size: 1em; margin: 0;">{{ errorMessage }}</p>
                        </div>

                        <!-- STEP 3: CONFIRMATION -->
                        <!-- STEP 3: CONFIRMATION -->
                        <div v-if="step === 3" style="text-align: left; padding: 0 20px;">
                            <p style="font-size: 1em; font-weight: bold; color: green;" v-if="!errorMessage">
                                ePrescribing setup saved.
                            </p>
                            <div v-if="submittedThumbprint || submittedOrgName" style="margin: 10px 0;">
                                <p style="font-size: 1em; margin: 4px 0;"><b>Organisation Name:</b> {{ submittedOrgName || '-' }}</p>
                                <p style="font-size: 1em; margin: 4px 0;"><b>Thumbprint:</b> {{ submittedThumbprint || '-' }}</p>
                                <p style="font-size: 0.9em; margin: 4px 0;">
                                    These are the details we will confirm with eRx to activate your ePrescribing
                                    account.
                                </p>
                            </div>
                            <p style="font-size: 1em;">
                                You can update these details or re-run this setup anytime from
                                <b>Settings &gt; Prescriber Details</b>.
                            </p>
                        </div>

                    </form>
                </div>

                <div class="modal-footer" style="display: flex; justify-content: space-between; padding: 5px 20px;">
                    <button v-if="step === 2" class="button black-button" @click="step = 1" :disabled="saving">
                        Back
                    </button>
                    <div v-else></div>

                    <div style="display: flex; grid-gap: 10px;">
                        <button v-if="step < 3" class="button black-button" @click="skip()" :disabled="saving">
                            Skip for now
                        </button>
                        <button v-if="step === 1" class="green-button" @click="step = 2">
                            Get Started
                        </button>
                        <button v-if="step === 2" class="green-button" @click="submit()" :disabled="saving">
                            {{ saving ? 'Saving...' : 'Save & Continue' }}
                        </button>
                        <button v-if="step === 3" class="green-button" @click="closeModal(true)">
                            Done
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    props: ['prescriberId'],
    emits: ['close'],
    data() {
        return {
            step: 1,
            entityId: '',
            certBase64: null,
            certFileName: '',
            certPassphrase: '',
            saving: false,
            errorMessage: '',
            submittedThumbprint: '',
            submittedOrgName: '',
        }
    },
    methods: {
        onCertFileSelected(e) {
            const file = e.target.files[0];
            if (!file) {
                this.certBase64 = null;
                this.certFileName = '';
                return;
            }
            this.certFileName = file.name;
            const reader = new FileReader();
            reader.onload = (event) => {
                // readAsDataURL result looks like "data:application/x-pkcs12;base64,<data>"
                const result = event.target.result || '';
                const commaIndex = result.indexOf(',');
                this.certBase64 = commaIndex >= 0 ? result.substring(commaIndex + 1) : result;
            };
            reader.readAsDataURL(file);
        },
        buildPayload() {
            const payload = {
                entity_id: this.entityId ? btoa(this.entityId + ':') : null,
                erx_nash_cert: null,
                erx_nash_cert_passphrase: null,
            };
            if (this.certBase64 && this.certPassphrase) {
                payload.erx_nash_cert = this.certBase64;
                payload.erx_nash_cert_passphrase = this.certPassphrase;
            }
            return payload;
        },
        submit() {
            this.errorMessage = '';
            this.saving = true;
            axios.post(`/person/${this.prescriberId}/erx-onboarding`, this.buildPayload(), this.$store.state.header)
                .then((res) => {
                    this.saving = false;
                    this.submittedThumbprint = res.data && res.data.erx_nash_cert_thumbprint ? res.data.erx_nash_cert_thumbprint : '';
                    this.submittedOrgName = res.data && res.data.erx_nash_cert_org_name ? res.data.erx_nash_cert_org_name : '';
                    this.step = 3;
                })
                .catch((err) => {
                    this.saving = false;
                    this.errorMessage = (err.response && err.response.data && err.response.data.message)
                        ? err.response.data.message
                        : 'There was an error saving your ePrescribing setup. Please check your certificate and passphrase and try again.';
                });
        },
        skip() {
            this.saving = true;
            // Skipping still stamps erx_onboarding_completed_at so the wizard does not
            // trigger again - it can always be re-run manually from Settings.
            axios.post(`/person/${this.prescriberId}/erx-onboarding`, { entity_id: null, erx_nash_cert: null, erx_nash_cert_passphrase: null }, this.$store.state.header)
                .then(() => {
                    this.saving = false;
                    this.closeModal(true);
                })
                .catch((err) => {
                    this.saving = false;
                    console.error('Error skipping ePrescribing setup:', err);
                    this.closeModal(false);
                });
        },
        closeModal(completed) {
            this.$emit('close', completed);
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/css/main.scss";

.action-modal {
    overflow: auto;
}
</style>
